import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  nanoid,
  type PayloadAction,
  type Update,
} from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import { EWidgetSection } from '#shared';

import type { RootState } from '../redux-store';
import type { WidgetId, WidgetType } from '../widgetManager';

export interface BlockMeta {
  id: WidgetId;
  sectionId?: EWidgetSection | null;
  instanceId: WidgetId;
  instanceIndex?: number; // For multi-instance widgets (e.g., 0=left, 1=right)
  visible?: boolean; // Controls block visibility (default: true)
  minH?: number;
  minW?: number;
  // Additional widget instances to render alongside the primary instance
  // Format: [{ instanceId, position: 'left' | 'right' }]
  additionalInstances?: Array<{
    instanceId: WidgetId;
    instanceIndex?: number;
    position: 'left' | 'right';
  }>;
  // Direction for additional instances layout (default: 'row')
  additionalInstancesDirection?: 'row' | 'column';
}

const adapter = createEntityAdapter<BlockMeta, WidgetId>({
  selectId: b => b.id,
});
export type BlockState = EntityState<BlockMeta, WidgetId>;

//TODO this slice and use instances directly
export const blockSlice = createSlice({
  name: 'layout/blocks',
  initialState: adapter.getInitialState(),
  reducers: {
    blocksUpdate(
      state,
      { payload }: PayloadAction<Update<BlockMeta, WidgetId>[]>,
    ) {
      adapter.updateMany(state, payload);
    },
    blockUpsert: {
      prepare(params: Partial<BlockMeta> & { instanceType?: WidgetType }) {
        let id: WidgetId;
        if (params.id || params.instanceId) {
          id = (params.id || params.instanceId) as WidgetId;
        } else if (params.instanceType) {
          id = `${params.instanceType}-${nanoid()}`;
        } else {
          throw new Error(
            'Either id, instanceId or instanceType must be provided to create a block',
          );
        }
        return {
          payload: {
            id,
            instanceId: id,
            ...(params.sectionId !== undefined
              ? { sectionId: params.sectionId }
              : {}),
            ...(params.instanceType !== undefined
              ? { instanceType: params.instanceType }
              : {}),
            ...(params.minH !== undefined ? { minH: params.minH } : {}),
            ...(params.minW !== undefined ? { minW: params.minW } : {}),
          },
        };
      },
      reducer: adapter.upsertOne,
    },
    blockRemove: adapter.removeOne,

    /**
     * Set block visibility
     */
    setBlockVisible(state, action: PayloadAction<WidgetId>) {
      const block = state.entities[action.payload];
      if (block) {
        block.visible = true;
      }
    },

    /**
     * Hide block
     */
    setBlockHidden(state, action: PayloadAction<WidgetId>) {
      const block = state.entities[action.payload];
      if (block) {
        block.visible = false;
      }
    },

    /**
     * Toggle block visibility
     */
    toggleBlockVisibility(state, action: PayloadAction<WidgetId>) {
      const block = state.entities[action.payload];
      if (block) {
        block.visible = !block.visible;
      }
    },

    /**
     * Set visibility for multiple blocks
     */
    setBlocksVisibility(
      state,
      action: PayloadAction<{ blockIds: WidgetId[]; visible: boolean }>,
    ) {
      action.payload.blockIds.forEach(blockId => {
        const block = state.entities[blockId];
        if (block) {
          block.visible = action.payload.visible;
        }
      });
    },

    /**
     * Replace entire blocks state (used for template application)
     */
    setBlocks(state, action: PayloadAction<Record<WidgetId, BlockMeta>>) {
      const blocksArray = Object.values(action.payload);
      adapter.setAll(state, blocksArray);
    },
  },
});

const selectors = adapter.getSelectors<{ blocks: BlockState }>(s => s.blocks);

export const blockSelectors = {
  useSelectBlockById: (id: WidgetId) =>
    useSelector((s: RootState) => selectors.selectById(s, id)),
  useSelectBlockAll: () =>
    useSelector((s: RootState) => selectors.selectAll(s)),
  useSelectBlockEntities: () =>
    useSelector((s: RootState) => selectors.selectEntities(s)),
  /**
   * Check if newCases aside widgets are visible within mainGeoMap block
   * Uses additionalInstances feature
   */
  useSelectNewCasesBlocksVisible: () =>
    useSelector((s: RootState) => {
      const allBlocks = selectors.selectAll(s);
      const blockEntities = selectors.selectEntities(s);
      const widgetEntities = s.widgetInstances.entities;
      const centerSection = s.sections.entities.center;

      let mainGeoMapBlock = allBlocks.find(
        block =>
          block.instanceId.startsWith('geo-map') && block.id === 'geo-map-main',
      );

      if (!mainGeoMapBlock && centerSection) {
        mainGeoMapBlock = centerSection.blockOrder
          .map(blockId => blockEntities[blockId])
          .find(
            block =>
              block &&
              (block.instanceId.startsWith('geo-map') ||
                widgetEntities[block.instanceId]?.type === 'geo-map'),
          );
      }

      return (
        mainGeoMapBlock?.additionalInstances &&
        mainGeoMapBlock.additionalInstances.length > 0
      );
    }),
};

export const blockReducer = blockSlice.reducer;
export const blockActions = blockSlice.actions;
